import { Link, useLoaderData } from 'react-router'

import {
    IconCurrencyDollar,
    Table,
    PageHeader, Button
} from '@/components'
import { AuthLayout } from '@/containers'
import { Invoice } from '@/models'
import { sessionStorage } from '@/services'

const breadcrumb = [
    <IconCurrencyDollar key={1} className="stroke-gray-500"/>,
    <Link key={2} to="/invoices">Invoice Records - Official Invoice Records</Link>,
    'Invoice Details'
]

export const InvoicesShow = () => {
    const { invoice } = useLoaderData() as { invoice: Invoice }

    const heading = <PageHeader
        title={`Invoice ${invoice.invoice_number}`}
        backUrl={`/invoices${sessionStorage.get('preservedQuery.invoices')}`}
        subtitle={<div className="flex flex-wrap justify-between gap-4 mt-2 text-gray-500">
            <ul className="flex flex-col gap-2">
                <li>
                    <span className="font-semibold">
                        Invoice Status:
                    </span> <span className="text-sm">
                        {invoice.payment_status.badge}
                    </span>
                </li>
                <li>
                    <span className="font-semibold">
                        Accounting Period:
                    </span> {invoice.accounting_period.format({ day: null, month: 'long', year: 'numeric' })}
                </li>
                <li><span className="font-semibold">Company:</span> {invoice.company_name}</li>
                <li><span className="font-semibold">Billing Type:</span> {invoice.type.title}</li>
            </ul>
            <div className="bg-white p-2 shadow-lg rounded-lg">
                <table className="w-full">
                    <tbody>
                        <tr>
                            <td className="pr-4 py-2 pl-1">Invoice Date:</td>
                            <td className="text-right pl-4 pr-1 py-2">{invoice.accounting_period.format()}</td>
                        </tr>
                        <tr>
                            <td className="pr-4 py-2 pl-1">Due Date:</td>
                            <td className="text-right pl-4 pr-1 py-2">{invoice.due_date?.format()}</td>
                        </tr>
                        <tr>
                            <td className="pr-4 py-2 pl-1">Billing Terms:</td>
                            <td className="text-right pl-4 pr-1 py-2">{invoice.invoice_term}</td>
                        </tr>
                        <tr className="font-semibold bg-primary-100">
                            <td className="pr-4 py-1 pl-1">Amount Due:</td>
                            <td className="text-right pl-4 pr-1 py-1">
                                {invoice.balance_due < 0
                                    ? `(${Math.abs(invoice.balance_due).money()})`
                                    : invoice.balance_due.money()}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>}
    />

    const rows = invoice.items?.map(item => ({
        id: item.id,
        warranty_product_name: item.warranty_product_name,
        policy_num: item.policy_number
            ? item.warranty_id
                ? <Button
                    href={`/warranties/${item.warranty_id}`}
                    design="btn-link"
                    className="text-primary-700 hover:underline justify-start"
                >
                    {item.policy_number}
                </Button>
                : item.policy_number
            : '...',
        panel_count: item.panel_count?.format() || '...',
        panel_count_raw: item.panel_count,
        system_size: item.system_size ? `${item.system_size.format()} kW` : '...',
        system_size_raw: item.system_size,
        contact_name: item.contact_name || '...',
        address: item.address || '...',
        total_price: invoice.total_price < 0
            ? `(${Math.abs(item.total_price).money()})` : item.total_price.money(),
        total_price_raw: item.total_price
    })) || []

    return <AuthLayout heading={heading} breadcrumb={breadcrumb}>
        <ul className="mt-6 border border-gray-300 rounded-lg text-gray-500 text-xs bg-gray-50 flex flex-wrap">
            <li className="px-6 py-3">
                <span className="font-semibold">Number of Panels:</span> {invoice.panel_total_count.format()}
            </li>
            <li className="px-6 py-3">
                <span className="font-semibold">Number of Policies:</span> {invoice.policy_total_count.format()}
            </li>
            <li className="px-6 py-3">
                <span className="font-semibold">Number of Batteries:</span> {invoice.battery_total_count.format()}
            </li>
        </ul>

        <Table
            className="mt-6"
            options={{ useQueryParams: false }}
            columns={[
                { field: 'warranty_product_name', title: 'Product' },
                { field: 'policy_num', title: 'Warranty Plan ID' },
                { field: 'panel_count', title: 'Panels' },
                { field: 'system_size', title: 'System Size' },
                { field: 'contact_name', title: 'Warranty Holder' },
                { field: 'address', title: 'Address' },
                { field: 'total_price', title: 'Total' }
            ]}
            rows={rows}
        />

        <div className="flex flex-col lg:flex-row gap-6 justify-between mt-6 text-gray-500">
            <div className="flex flex-col gap-2 grow text-xs">
                <p>
                    <span className="font-semibold">📌 Invoice Details:</span> For invoices before 2025 or credit/debit
                    memos, details may be limited in Daybreak. This display is for reference only. For questions or
                    records, contact <a href="mailto:accounting@solarinsure.com">accounting@solarinsure.com</a> or your
                    account manager.
                </p>
                <p>
                    <span className="font-semibold">💳 Payments:</span> Payments are not processed in Daybreak. Check
                    your inbox for emails
                    from <a href="mailto:accounting@solarinsure.com">accounting@solarinsure.com</a> for payment details.
                    Payments are securely handled via a secure external payment gateway.
                </p>
            </div>

            <div className="bg-white p-2 shadow-lg rounded-lg">
                <table className="w-full">
                    <tbody>
                        <tr className="font-semibold">
                            <td className="pr-4 py-2 pl-1">Total:</td>
                            <td className="text-right pl-4 pr-1 py-2">
                                {invoice.total_price < 0
                                    ? `(${Math.abs(invoice.total_price).money()})` : invoice.total_price.money()}
                            </td>
                        </tr>
                        <tr>
                            <td className="pr-4 py-2 pl-1">Amount Paid:</td>
                            <td className="text-right pl-4 pr-1 py-2">
                                {invoice.amount_paid < 0
                                    ? `(${Math.abs(invoice.amount_paid).money()})` : invoice.amount_paid.money()}
                            </td>
                        </tr>
                        <tr className="font-semibold bg-primary-100">
                            <td className="pr-4 py-1 pl-1">Amount Due:</td>
                            <td className="text-right pl-4 pr-1 py-1">
                                {invoice.balance_due < 0
                                    ? `(${Math.abs(invoice.balance_due).money()})` : invoice.balance_due.money()}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </AuthLayout>
}
